import { PrimaryButton, SecondaryButton } from 'components/theme/Button/Button';
import OverflowOptionButton from 'components/molecules/OverflowButtonGroup/OverflowOptionButton';
import { MINUTES_PER_DAY } from 'utils/date/minutesPerDay';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BottomDrawer } from './BottomDrawer';
import { SelectTriggerButton } from './SelectTriggerButton';

/** The element the embed's styles are scoped to; see `index.js`, which namespaces every rule under it. */
const EMBED_ROOT_ID = 'kouto-embed-root';

/** Noon, as minutes into the day: a time before it is a morning one. */
const NOON_MINUTES = MINUTES_PER_DAY / 2;

export interface BottomSheetTimeOption {
  /** What the button says, such as "8:00 AM". */
  label: string;
  /** The time of day the option stands for, "HH:mm:ss", which decides Morning or Afternoon. */
  timeOfDay: string;
  /** What is handed back when it is chosen. */
  value: string;
}

interface BottomSheetTimePickerProps {
  disabled?: boolean;
  /** The drawer's title, such as "Start time". */
  heading: string;
  options: readonly BottomSheetTimeOption[];
  /** Fired with the chosen value when Save is pressed. */
  onSave: (value: string) => void;
  /** What the field says before anything is chosen. */
  placeholder: string;
  /** The value already chosen, if any. */
  selectedValue?: string;
  testId: string;
}

const toMinutesOfDay = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * A time field that opens a drawer of buttons, under Morning and Afternoon, with Clear and Save beneath.
 *
 * What is tapped is only a draft until Save: closing the drawer leaves the field as it was, and Clear
 * empties the draft so another can be made.
 */
export const BottomSheetTimePicker = ({
  disabled = false,
  heading,
  options,
  onSave,
  placeholder,
  selectedValue,
  testId,
}: BottomSheetTimePickerProps) => {
  const { t: translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | undefined>(selectedValue);
  // The drawer is fixed to the screen, but it is opened from inside another drawer whose transform makes
  // it the containing block of anything fixed within it: left there, this one is positioned and clipped by
  // that sheet and its top cannot be scrolled to. It is rendered into the embed's root instead, which is
  // inside the embed's styles and outside every sheet.
  const portalContainer = useMemo(
    () => document.getElementById(EMBED_ROOT_ID) ?? document.body,
    [],
  );

  const selectedLabel = options.find(
    ({ value }) => value === selectedValue,
  )?.label;

  const handleOpen = () => {
    setDraft(selectedValue);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleClear = () => setDraft(undefined);
  const handleSave = () => {
    if (draft !== undefined) {
      onSave(draft);
    }
    setOpen(false);
  };

  const morning = options.filter(
    ({ timeOfDay }) => toMinutesOfDay(timeOfDay) < NOON_MINUTES,
  );
  const afternoon = options.filter(
    ({ timeOfDay }) => toMinutesOfDay(timeOfDay) >= NOON_MINUTES,
  );

  const renderGroup = (
    label: string,
    group: readonly BottomSheetTimeOption[],
    groupTestId: string,
  ) =>
    group.length > 0 && (
      <Group data-testid={groupTestId}>
        <GroupLabel>{label}</GroupLabel>
        <Grid role="group" aria-label={label}>
          {group.map((option) => (
            <OverflowOptionButton
              isActive={draft === option.value}
              isTimeChip
              key={option.value}
              label={option.label}
              onSelect={setDraft}
              testId={`${testId}-option-${option.value}`}
              value={option.value}
            />
          ))}
        </Grid>
      </Group>
    );

  return (
    <>
      <SelectTriggerButton
        aria-label={heading}
        data-testid={testId}
        disabled={disabled}
        onClick={handleOpen}
      >
        {selectedLabel ?? placeholder}
      </SelectTriggerButton>
      {createPortal(
        <BoxSizingScope>
          <BottomDrawer
            size="full"
            open={open}
            heading={heading}
            onClose={handleClose}
          >
            <Body>
              {renderGroup(translate('morning'), morning, `${testId}-morning`)}
              {renderGroup(
                translate('afternoon'),
                afternoon,
                `${testId}-afternoon`,
              )}
            </Body>
            <Actions>
              <SecondaryButton
                data-testid={`${testId}-clear`}
                onClick={handleClear}
                type="button"
              >
                {translate('clear')}
              </SecondaryButton>
              <PrimaryButton
                data-testid={`${testId}-save`}
                disabled={draft === undefined}
                onClick={handleSave}
                type="button"
              >
                {translate('save')}
              </PrimaryButton>
            </Actions>
          </BottomDrawer>
        </BoxSizingScope>,
        portalContainer,
      )}
    </>
  );
};

/**
 * Sizing as the rest of the page has it. Outside the page's own container a drawer's width of 100% plus
 * its padding runs past the screen, so the drawer brings the rule with it.
 */
const BoxSizingScope = styled.div`
  &,
  & *,
  & *::before,
  & *::after {
    box-sizing: border-box;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-70);
`;

/** One cell to a time chip, which carries its own size; as many to a row as the drawer holds. */
const Grid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, 80px);
  width: 100%;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
  width: 100%;
`;
